import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { Ticket, Plus, Edit, Loader2, Calendar, Trophy, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminRafflesPage({ embedded = false }) {
  const { api } = useAuth();
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRaffle, setEditingRaffle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prize: '',
    start_date: '',
    end_date: '',
    ticket_cost: 10,
    max_tickets_per_family: 10,
    terms: '',
    active: true,
  });

  useEffect(() => {
    fetchRaffles();
  }, []);

  const fetchRaffles = async () => {
    try {
      const response = await api.get('/raffles/');
      setRaffles(response.data.results || response.data);
    } catch (error) {
      console.error('Failed to fetch raffles:', error);
      toast.error('Failed to load raffles');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      prize: '',
      start_date: '',
      end_date: '',
      ticket_cost: 10,
      max_tickets_per_family: 10,
      terms: '',
      active: true,
    });
    setEditingRaffle(null);
  };

  const handleOpenDialog = (raffle = null) => {
    if (raffle) {
      setEditingRaffle(raffle);
      setFormData({
        name: raffle.name || '',
        description: raffle.description || '',
        prize: raffle.prize || '',
        start_date: raffle.start_date ? raffle.start_date.split('T')[0] : '',
        end_date: raffle.end_date ? raffle.end_date.split('T')[0] : '',
        ticket_cost: raffle.ticket_cost || 10,
        max_tickets_per_family: raffle.max_tickets_per_family || 10,
        terms: raffle.terms || '',
        active: raffle.active ?? true,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a raffle name');
      return;
    }
    if (!formData.prize) {
      toast.error('Please enter a prize');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please enter start and end dates');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
      };

      if (editingRaffle) {
        await api.put(`/raffles/${editingRaffle.id}/`, payload);
        toast.success('Raffle updated');
      } else {
        await api.post('/raffles/', payload);
        toast.success('Raffle created');
      }
      await fetchRaffles();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Save error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save raffle');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeRaffles = raffles.filter(r => r.active);
  const totalEntries = raffles.reduce((sum, r) => sum + (r.total_entries || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-raffles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Raffles</h1>
          <p className="text-muted-foreground">Manage raffle prizes and drawings</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={() => handleOpenDialog()} data-testid="add-raffle-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Raffle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRaffle ? 'Edit Raffle' : 'Add New Raffle'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Holiday Raffle 2026"
                  data-testid="raffle-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="prize">Prize *</Label>
                <Input
                  id="prize"
                  value={formData.prize}
                  onChange={(e) => setFormData({ ...formData, prize: e.target.value })}
                  placeholder="Nintendo Switch Bundle"
                  data-testid="raffle-prize-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Describe the raffle and prize..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    data-testid="raffle-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    data-testid="raffle-end-date"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ticket_cost">Ticket Cost (points)</Label>
                  <Input
                    id="ticket_cost"
                    type="number"
                    min="1"
                    value={formData.ticket_cost}
                    onChange={(e) => setFormData({ ...formData, ticket_cost: parseInt(e.target.value) || 10 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_tickets">Max Tickets/Family</Label>
                  <Input
                    id="max_tickets"
                    type="number"
                    min="1"
                    value={formData.max_tickets_per_family}
                    onChange={(e) => setFormData({ ...formData, max_tickets_per_family: parseInt(e.target.value) || 10 })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="terms">Terms & Conditions</Label>
                <Textarea
                  id="terms"
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                  rows={2}
                  placeholder="Must be enrolled student to participate..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="active">Active (visible to users)</Label>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-raffle-btn">
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {editingRaffle ? 'Save Changes' : 'Create Raffle'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Ticket className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{raffles.length}</p>
                <p className="text-sm text-muted-foreground">Total Raffles</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Trophy className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{activeRaffles.length}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{totalEntries}</p>
                <p className="text-sm text-muted-foreground">Total Entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {raffles.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <Ticket className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No raffles yet</h2>
            <p className="text-muted-foreground">Create your first raffle to engage families.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {raffles.map((raffle) => (
            <Card key={raffle.id} className="card-dashboard" data-testid={`raffle-card-${raffle.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg">{raffle.name}</h3>
                  <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(raffle)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mb-3">
                  <Badge variant="secondary" className="mb-2">
                    <Trophy className="h-3 w-3 mr-1" />
                    {raffle.prize || 'Prize TBA'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {raffle.description || 'No description'}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Ticket Cost:</span>
                    <span className="font-medium">{raffle.ticket_cost} pts</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Period:</span>
                    <span className="font-medium text-xs">
                      {formatDate(raffle.start_date)} - {formatDate(raffle.end_date)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Entries:</span>
                    <span className="font-medium">{raffle.total_entries || 0}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <Badge variant={raffle.active ? 'default' : 'secondary'}>
                    {raffle.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
